<template>
  <v-app>
    <v-main>
      <v-container fluid class="login-container">
        <div class="language-toggle-wrapper">
         <v-btn-toggle v-model="currentLanguage" mandatory variant="outlined" density="comfortable" class="language-toggle">
                <v-btn value="en" @click="changeLanguage('en')" :class="{ 'active-lang': currentLanguage === 'en' }">
                  <v-img :src="usa_flag" width="24" height="18" class="flag-img" />
                  <span class="ml-1">EN</span>
                </v-btn>
                <v-btn value="de" @click="changeLanguage('de')" :class="{ 'active-lang': currentLanguage === 'de' }">
                  <v-img :src="german_flag" width="24" height="18" class="flag-img" />
                  <span class="ml-1">DE</span>
                </v-btn>
              </v-btn-toggle>
        </div>

        <v-row align="center" justify="center" class="fill-height">
          <v-col cols="12" sm="8" md="6" lg="4" xl="3">
            
            <div class="text-center mb-8">
              <h1 class="text-h3 text-white font-weight-medium mb-2">
                {{ $t ? $t('landing-page.welcome') : 'Welcome' }}
              </h1>
              <p class="text-subtitle-1 text-white">
                {{ $t ? $t('landing-page.signin') : 'Sign in to your account' }}
              </p>
            </div>

            <v-card 
              elevation="12" 
              rounded="xl" 
              class="mx-auto login-card mb-4"
            >
              <v-card-text class="pa-6 pa-sm-8 pa-md-10">
                
                <div class="text-center mb-6">
                  <v-img
                    :src="loginLogo"
                    max-height="100"
                    max-width="100"
                    class="mx-auto"
                    contain
                  />
                </div>

                <v-btn
                  block
                  size="large"
                  color="primary"
                  variant="elevated"
                  class="mb-6"
                  @click="login"
                >
                  <v-icon start>mdi-lock-outline</v-icon>
                  {{ $t ? $t('landing-page.authenticate') : 'Authenticate' }}
                </v-btn>

                <v-row dense class="text-center">
                  <v-col cols="6">
                    <v-chip size="small" variant="tonal" color="primary">
                      <v-icon start size="small">mdi-shield-check</v-icon>
                      {{ $t ? $t('landing-page.secure') : 'Secure' }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6">
                    <v-chip size="small" variant="tonal" color="primary">
                      <v-icon start size="small">mdi-devices</v-icon>
                      {{ $t ? $t('landing-page.multiplatform') : 'Multi-Platform' }}
                    </v-chip>
                  </v-col>
                </v-row>

              </v-card-text>
            </v-card>

            <v-card 
              elevation="12" 
              rounded="xl" 
              class="mx-auto demo-card"
            >
              <v-card-text class="pa-4 pa-sm-6">
                <div class="text-center mb-3">
                  <p class="text-body-2 text-medium-emphasis">
                    {{ $t ? $t('landing-page.noAccount') : "Don't have an account?" }}
                  </p>
                </div>
                
                <v-btn
                  block
                  size="large"
                  color="secondary"
                  variant="outlined"
                  class="try-demo-button mb-4"
                  @click="goToRequestDemo"
                >
                  <v-icon start>mdi-rocket-launch-outline</v-icon>
                  {{ $t ? $t('landing-page.tryDemo') : 'Try Our Demo' }}
                </v-btn>

                <v-divider class="my-4"></v-divider>

                <div class="text-center mb-3">
                  <p class="text-caption text-medium-emphasis">
                    {{ $t ? $t('landing-page.downloadApp') : 'Download Mobile App' }}
                  </p>
                </div>

                <div class="app-buttons-container">
                  <v-btn
                    class="store-button google-play-button"
                    @click="openPlayStore"
                    variant="outlined"
                  >
                    <template v-slot:default>
                      <div class="store-button-content">
                        <svg class="store-icon" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="playGradient1" x1="21.8" y1="33.3" x2="0.5" y2="12" gradientUnits="userSpaceOnUse">
                              <stop offset="0" stop-color="#00a0ff"/>
                              <stop offset="0.007" stop-color="#00a1ff"/>
                              <stop offset="0.26" stop-color="#00beff"/>
                              <stop offset="0.512" stop-color="#00d2ff"/>
                              <stop offset="0.76" stop-color="#00dfff"/>
                              <stop offset="1" stop-color="#00e3ff"/>
                            </linearGradient>
                            <linearGradient id="playGradient2" x1="33.8" y1="22.2" x2="9.7" y2="46.3" gradientUnits="userSpaceOnUse">
                              <stop offset="0" stop-color="#ffe000"/>
                              <stop offset="0.409" stop-color="#ffbd00"/>
                              <stop offset="0.775" stop-color="#ffa500"/>
                              <stop offset="1" stop-color="#ff9c00"/>
                            </linearGradient>
                            <linearGradient id="playGradient3" x1="24.8" y1="48.6" x2="2.1" y2="25.9" gradientUnits="userSpaceOnUse">
                              <stop offset="0" stop-color="#ff3a44"/>
                              <stop offset="1" stop-color="#c31162"/>
                            </linearGradient>
                            <linearGradient id="playGradient4" x1="-0.1" y1="1.7" x2="17.5" y2="19.3" gradientUnits="userSpaceOnUse">
                              <stop offset="0" stop-color="#32a071"/>
                              <stop offset="0.069" stop-color="#2da771"/>
                              <stop offset="0.476" stop-color="#15cf74"/>
                              <stop offset="0.801" stop-color="#06e775"/>
                              <stop offset="1" stop-color="#00f076"/>
                            </linearGradient>
                          </defs>
                          <path fill="url(#playGradient1)" d="M6,42.6L27.6,21L6,0.4c-0.5,0.3-0.9,0.8-0.9,1.4v39.4C5.1,41.8,5.5,42.3,6,42.6z"/>
                          <path fill="url(#playGradient2)" d="M33.6,28.4l-6-3.4L6,46.6c0.5,0.3,1.1,0.3,1.6,0L33.6,28.4z"/>
                          <path fill="url(#playGradient3)" d="M33.6,16.6L7.6,0.4C7.1,0.1,6.5,0.1,6,0.4L27.6,21L33.6,16.6z"/>
                          <path fill="url(#playGradient4)" d="M33.6,28.4l6-3.4c0.5-0.3,0.9-0.9,0.9-1.5s-0.4-1.2-0.9-1.5l-6-3.4L27.6,21L33.6,28.4z"/>
                        </svg>
                        <div class="store-button-text">
                          <div class="store-button-subtitle">{{ $t ? $t('landing-page.getItOn') : 'GET IT ON' }}</div>
                          <div class="store-button-title">Google Play</div>
                        </div>
                      </div>
                    </template>
                  </v-btn>

                  <v-btn
                    class="store-button app-store-button"
                    @click="openAppStore"
                    variant="outlined"
                  >
                    <template v-slot:default>
                      <div class="store-button-content">
                        <svg class="store-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" fill="currentColor"/>
                        </svg>
                        <div class="store-button-text">
                          <div class="store-button-subtitle">{{ $t ? $t('landing-page.downloadOn') : 'Download on' }}</div>
                          <div class="store-button-title">App Store</div>
                        </div>
                      </div>
                    </template>
                  </v-btn>
                </div>

              </v-card-text>
            </v-card>

          </v-col>
        </v-row>

        <div class="background-layer">
          <v-img
            :src="backgroundImage"
            cover
            class="background-image"
          >
            <div class="background-overlay"></div>
          </v-img>
        </div>

      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getStoredCustomer, setStoredCustomer } from '@/configLoader'
import { setStoredItem, setCookie, getCookie } from '@/services/utils'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { getLoginBackgroundImage, getLoginLogoImage } from '@/assets/Branding/branding.js'
import { useOAuthService } from '@/services/OAuthService'
import usa_flag from '@/assets/General/usa_flag.svg'
import german_flag from '@/assets/General/german_flag.svg'

export default {
  name: 'LoginPage',
  setup() {
    const router = useRouter()
    const { locale } = useI18n()
    const { authenticate } = useOAuthService()
    
    const backgroundImage = ref('')
    const loginLogo = ref('')
    const selectedCustomer = ref('')
    const currentLanguage = ref('de')

    const detectAndSetLanguage = async () => {
      const savedLanguage = await getCookie('language')
      if (savedLanguage) {
        currentLanguage.value = savedLanguage
        locale.value = savedLanguage
        return
      }

      const browserLang = navigator.language || navigator.userLanguage
      if (browserLang) {
        const langCode = browserLang.toLowerCase().split('-')[0]
        if (langCode === 'en' || langCode === 'de') {
          currentLanguage.value = langCode
          locale.value = langCode
          await setCookie('language', langCode, 365)
          return
        }
      }

      currentLanguage.value = 'de'
      locale.value = 'de'
      await setCookie('language', 'de', 365)
    }

    const changeLanguage = async (lang) => {
      currentLanguage.value = lang
      locale.value = lang
      await setCookie('language', lang, 365)
    }

    const initializeApp = async () => {
      const hostname = window.location.hostname.toLowerCase()

      let appName = ''
      if (Capacitor.isNativePlatform()) {
        try {
          const { name } = await App.getInfo()
          appName = name ? name.toLowerCase() : ''
        } catch (err) {
          console.warn('[Login] Failed to get app info:', err)
        }
      }

      let detectedCustomer = ''
      if (hostname.includes('hbc') || hostname?.includes('hammerbrooklyn') || appName.includes('hammerbrooklyn')) {
        detectedCustomer = 'HBC'
      } else if (hostname.includes('pia.qa') || hostname.includes('pia.staging') || appName.includes('pia')) {
        detectedCustomer = 'PIA'
      } else if (hostname === 'localhost' || /^192\.168\.\d+\.\d+$/.test(hostname)) {
        detectedCustomer = 'PIA'
      } else {
        detectedCustomer = await getStoredCustomer()
      }

      const building = detectedCustomer?.toLowerCase()?.includes('pia')
        ? 'Pia Building'
        : 'Factory Hammerbrooklyn'

      await setStoredItem('selectedBuilding', building)
      selectedCustomer.value = detectedCustomer
      await setStoredCustomer(detectedCustomer)

      backgroundImage.value = await getLoginBackgroundImage() || ''
      loginLogo.value = await getLoginLogoImage() || ''
    }

    const login = async () => {
      await authenticate()
    }

    const goToRequestDemo = () => {
      router.push({ name: 'RequestDemo' })
    }

    const openPlayStore = () => {
      const lang = currentLanguage.value === 'en' ? 'en' : 'de_DE'
      const playStoreUrl = `https://play.google.com/store/apps/details?id=io.pinestack.pia&hl=${lang}`
      window.open(playStoreUrl, '_blank')
    }

    const openAppStore = () => {
      const appStoreUrl = 'https://apps.apple.com/by/app/pia-by-pinestack/id6746252933'
      window.open(appStoreUrl, '_blank')
    }

    onMounted(async () => {
      await detectAndSetLanguage()
      await initializeApp()
    })

    return {
      backgroundImage,
      loginLogo,
      selectedCustomer,
      currentLanguage,
      login,
      goToRequestDemo,
      changeLanguage,
      openPlayStore,
      openAppStore,
      usa_flag,
      german_flag
    }
  }
}
</script>

<style scoped>
.login-container {
  position: relative;
  min-height: 100vh;
  padding: 16px;
}

.flag-img {
  border-radius: 4px;
  object-fit: cover;
}

.fill-height {
  min-height: 100vh;
}

.language-toggle-wrapper {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

.language-toggle {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.language-toggle .v-btn {
  color: #64748b !important;
  transition: all 0.3s ease;
}

.language-toggle .v-btn.active-lang {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(21, 101, 192, 0.15)) !important;
  color: #2196F3 !important;
  font-weight: 600;
}

.language-toggle .v-btn:hover {
  background: rgba(33, 150, 243, 0.1) !important;
}

.login-card {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px);
  max-width: 480px;
}

.demo-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  max-width: 480px;
}

.try-demo-button {
  text-transform: none;
  letter-spacing: 0.5px;
  font-weight: 600;
}

/* App Store Buttons Container */
.app-buttons-container {
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  justify-content: space-between;
}

/* Store Button Base Styles */
.store-button {
  flex: 1;
  min-width: 0;
  height: 48px !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  border: 1.5px solid #d1d5db !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: none !important;
}

.store-button:hover {
  background: #f9fafb !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-color: #9ca3af !important;
}

.store-button .v-btn__content {
  width: 100%;
  height: 100%;
}

.store-button :deep(.v-btn__content) {
  width: 100%;
  height: 100%;
}

.store-button-content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  height: 100%;
}

.store-icon {
  width: 24px;
  height: 24px;
  min-width: 24px;
  flex-shrink: 0;
  opacity: 1 !important;
}

.google-play-button .store-icon {
  color: #000000;
  fill: currentColor;
}

.app-store-button .store-icon {
  color: #000000;
  fill: currentColor;
}

.store-button-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  line-height: 1.1;
  opacity: 1 !important;
}

.store-button-subtitle {
  font-size: 8px;
  font-weight: 400;
  color: #000000 !important;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 1px;
  opacity: 1 !important;
}

.store-button-title {
  font-size: 14px;
  font-weight: 600;
  color: #000000 !important;
  letter-spacing: 0.2px;
  opacity: 1 !important;
}

.background-layer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.background-image {
  width: 100%;
  height: 100%;
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.3), rgba(21, 101, 192, 0.25));
  backdrop-filter: blur(2px);
}

.v-row {
  position: relative;
  z-index: 1;
}

@media (max-width: 599px) {
  .login-container {
    padding: 12px;
  }

  .language-toggle-wrapper {
    top: 12px;
    right: 12px;
  }

  .language-toggle .v-btn {
    font-size: 13px;
    padding: 6px 10px;
  }

  .store-button {
    height: 44px !important;
    padding: 0 8px !important;
  }

  .store-icon {
    width: 20px;
    height: 20px;
    min-width: 20px;
  }

  .store-button-content {
    gap: 6px;
  }

  .store-button-subtitle {
    font-size: 7px;
  }

  .store-button-title {
    font-size: 12px;
  }
}

@media (max-width: 400px) {
  .store-button {
    height: 40px !important;
    padding: 0 6px !important;
  }

  .store-icon {
    width: 18px;
    height: 18px;
    min-width: 18px;
  }

  .store-button-content {
    gap: 4px;
  }

  .store-button-subtitle {
    font-size: 6px;
  }

  .store-button-title {
    font-size: 11px;
  }
}
</style>